'use client';

import { useLocale } from 'next-intl';
import {
  ComponentPropsWithRef,
  ComponentRef,
  forwardRef,
  useMemo,
  useState,
  MouseEvent,
} from 'react';

import { Link } from '@/i18n/routing';
import { defaultLocale as defaultLocaleCode } from '@/i18n/locales';
import { cn } from '@ecom/helper-react-class-combine';

type NextLinkProps = ComponentPropsWithRef<typeof Link>;

interface PrefetchOptions {
  prefetchOnHover?: boolean;
}

type Props = NextLinkProps & PrefetchOptions;

export const InternalLink = forwardRef<ComponentRef<'a'>, Props>(
  (
    {
      href,
      prefetch = true,
      prefetchOnHover = false,
      children,
      className,
      locale,
      onMouseEnter,
      ...rest
    },
    ref,
  ) => {
    const activeLocale = useLocale();
    const [hovered, setHovered] = useState(false);
    const finalLocale = locale ?? activeLocale;
    const localeForLink = finalLocale === defaultLocaleCode ? undefined : finalLocale;

    const effectivePrefetch = useMemo(() => {
      return prefetchOnHover ? hovered : prefetch;
    }, [prefetchOnHover, hovered, prefetch]);

    const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
      onMouseEnter?.(e);
      if (prefetchOnHover) setHovered(true); // one-shot: once hovered, keep it true
    };

    return (
      <Link
        className={cn(
          'focus-visible:ring-primary/20 hover:text-primary focus-visible:ring-4 focus-visible:outline-hidden',
          className,
        )}
        href={href}
        locale={localeForLink}
        onMouseEnter={handleMouseEnter}
        prefetch={effectivePrefetch}
        ref={ref}
        {...rest}
      >
        {children}
      </Link>
    );
  },
);

InternalLink.displayName = 'InternalLink';
